"use client";

import { Mail, MapPin, Phone, LucideIcon } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { motion } from "framer-motion";
import { PageBackground } from "@/components/ui/page-background";
import { pageTheme } from "@/lib/theme/page-theme";

export function ContactClient() {
  const { dictionary: t } = useLanguage();

  return (
    <div className={pageTheme.wrapperContact}>
      <PageBackground />

      {/* Ambient decorative orbs */}
      <div aria-hidden="true" className="fixed top-1/4 -right-48 h-96 w-96 rounded-full opacity-15 blur-[130px] pointer-events-none bg-orange-300 dark:bg-orange-600 z-0" />
      <div aria-hidden="true" className="fixed bottom-1/3 -left-48 h-80 w-80 rounded-full opacity-10 blur-[130px] pointer-events-none bg-orange-400 dark:bg-orange-500 z-0" />

      <div className="relative z-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 w-full">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              className={pageTheme.pageTitle}
            >
              {t.kontakt.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className={pageTheme.pageDesc + " max-w-2xl mx-auto"}
            >
              {t.kontakt.subtitle}
            </motion.p>
          </div>

          {/* Card Container Layout */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={pageTheme.contactCard}
          >

            {/* Contact Info Panel */}
            <div className="relative w-full lg:w-1/2 p-8 sm:p-12 lg:p-16 flex flex-col justify-center overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-36 top-1/2 h-[80%] -translate-y-1/2 opacity-35 dark:opacity-[0.35]"
              >
                <div
                  className="h-full aspect-[2048/1822] bg-zinc-400 dark:bg-zinc-700 [mask-image:url('/media/logos/A-logo.svg'),radial-gradient(circle_at_86%_14%,black_34%,transparent_100%)] [mask-size:contain,100%_100%] [mask-repeat:no-repeat,no-repeat] [mask-position:center,center] [mask-mode:alpha] [mask-composite:intersect] [-webkit-mask-image:url('/media/logos/A-logo.svg'),radial-gradient(circle_at_86%_14%,black_34%,transparent_100%)] [-webkit-mask-size:contain,100%_100%] [-webkit-mask-repeat:no-repeat,no-repeat] [-webkit-mask-position:center,center]"
                />
              </div>

              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-8">{t.kontakt.dataTitle}</h2>

                <ul className="flex flex-col space-y-8">
                  {([
                    { icon: MapPin, title: t.kontakt.locationTitle, content: <span>{t.kontakt.locationValue}</span> },
                    { icon: Phone, title: t.kontakt.phoneTitle, content: <a href="tel:+48500076868" className="text-zinc-900 dark:text-zinc-100 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">+48 500 076 868</a> },
                    { icon: Mail, title: t.kontakt.emailTitle, content: <a href="mailto:kontakt@automatyka-cnc.pl" className="text-zinc-900 dark:text-zinc-100 hover:text-orange-600 dark:hover:text-orange-400 transition-colors break-all">kontakt@automatyka-cnc.pl</a> },
                  ] as { icon: LucideIcon; title: string; content: React.ReactNode }[]).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-4 group list-none">
                      <div 
                        aria-hidden="true"
                        className={pageTheme.iconContainer + " group-hover:scale-110 transition-transform"}
                      >
                        <item.icon className="h-6 w-6" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">{item.title}</span>
                        <div className="mt-1 text-lg font-medium text-zinc-900 dark:text-zinc-100">{item.content}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-12 pt-8 border-t border-stone-200/60 dark:border-zinc-700/50">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 font-semibold mb-4">{t.kontakt.socialTitle}</p>
                  <div className="flex gap-4">
                    <a href="https://pl.linkedin.com/in/dariusz-kr%C3%B3l-184bb3115" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 font-medium">LinkedIn</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map View */}
            <div className={`w-full lg:w-1/2 min-h-[400px] lg:min-h-full relative ${pageTheme.contactMapBg}`}>
              <iframe
                src="https://maps.google.com/maps?q=Łukowica&t=&z=13&ie=UTF8&iwloc=&z=7&output=embed"
                className="absolute inset-0 w-full h-full border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mapa Dojazdu Łukowica"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
